import { getAuthHeaders, handleAuthError } from "./auth-api";
import type {
  UpsertProfilePayload,
  ReplaceAccessRulesPayload,
  ExportProfilesPayload,
  CreateFieldControlPayload,
  CreateShortcutPayload,
  UpdateMasterControlPayload,
  CmAckResponse,
} from "../types/control-management";

let baseUrl = "";
const API_PREFIX = "/api/v1";

export function setControlManagementBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

async function request<T>(
  method: "POST" | "PUT" | "DELETE",
  endpoint: string,
  body?: unknown,
): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    const message =
      (errorBody && typeof errorBody.message === "string"
        ? errorBody.message
        : null) ?? `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  return response.json();
}

// --- Profiles ---

export function createProfile(
  shopId: string,
  body: UpsertProfilePayload,
): Promise<CmAckResponse> {
  return request<CmAckResponse>(
    "POST",
    `/access-control/profiles/${encodeURIComponent(shopId)}`,
    body,
  );
}

export function updateProfile(
  shopId: string,
  profile: string,
  body: UpsertProfilePayload,
): Promise<CmAckResponse> {
  return request<CmAckResponse>(
    "PUT",
    `/access-control/profiles/${encodeURIComponent(shopId)}/${encodeURIComponent(profile)}`,
    body,
  );
}

// --- Access Rules ---

export function replaceAccessRules(
  shopId: string,
  profile: string,
  body: ReplaceAccessRulesPayload,
): Promise<CmAckResponse> {
  return request<CmAckResponse>(
    "PUT",
    `/access-control/profiles/${encodeURIComponent(shopId)}/${encodeURIComponent(profile)}/access`,
    body,
  );
}

// --- Export ---

export function exportProfiles(
  body: ExportProfilesPayload,
): Promise<CmAckResponse> {
  return request<CmAckResponse>(
    "POST",
    `/access-control/profiles/export`,
    body,
  );
}

// --- Field Control ---

export function createFieldControl(
  body: CreateFieldControlPayload,
): Promise<CmAckResponse> {
  return request<CmAckResponse>("POST", `/access-control/fields`, body);
}

export function deleteFieldControl(field: string): Promise<CmAckResponse> {
  return request<CmAckResponse>(
    "DELETE",
    `/access-control/fields/${encodeURIComponent(field)}`,
  );
}

// --- Master Control ---

export function updateMasterControl(
  body: UpdateMasterControlPayload,
): Promise<CmAckResponse> {
  return request<CmAckResponse>("PUT", `/access-control/masters`, body);
}

// --- Shortcuts ---

export function createShortcut(
  body: CreateShortcutPayload,
): Promise<CmAckResponse> {
  return request<CmAckResponse>("POST", `/access-control/shortcuts`, body);
}

export function deleteShortcut(keyId: number): Promise<CmAckResponse> {
  return request<CmAckResponse>("DELETE", `/access-control/shortcuts/${keyId}`);
}
